// Movimento do site público (mesma linguagem dos sites da DifLabs), sem biblioteca: IntersectionObserver + CSS.
// As transições ficam em home.css; com prefers-reduced-motion tudo aparece já no estado final.
import { Fragment, useEffect, useRef, useState, type CSSProperties, type ElementType, type ReactNode } from 'react'

export const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Fica true quando o elemento entra na tela (uma vez só, por padrão). */
export function useInView<T extends Element>(opts: { once?: boolean; rootMargin?: string; threshold?: number } = {}) {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  const { once = true, rootMargin = '0px 0px -12% 0px', threshold = 0 } = opts
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return }
    const io = new IntersectionObserver(([e]) => {
      if (e!.isIntersecting) { setInView(true); if (once) io.disconnect() } else if (!once) setInView(false)
    }, { rootMargin, threshold })
    io.observe(el)
    return () => io.disconnect()
  }, [once, rootMargin, threshold])
  return [ref, inView] as const
}

/** Sobe e aparece ao entrar na tela. `delay` em ms. */
export function Reveal({ children, delay = 0, as: Tag = 'div', className = '', style }: { children: ReactNode; delay?: number; as?: ElementType; className?: string; style?: CSSProperties }) {
  const [ref, inView] = useInView<HTMLElement>()
  return <Tag ref={ref} className={`sh-reveal ${inView ? 'is-in' : ''} ${className}`} style={{ ...style, '--d': `${delay}ms` } as CSSProperties}>{children}</Tag>
}

/**
 * Palavras que sobem de trás de uma máscara, uma a uma.
 * `*palavra*` sai na cor de destaque; `_palavra_` ganha o traço desenhado embaixo.
 */
export function Words({ text, as: Tag = 'h2', className = '', delay = 0, immediate = false }: { text: string; as?: ElementType; className?: string; delay?: number; immediate?: boolean }) {
  const [ref, inView] = useInView<HTMLElement>()
  const on = immediate || inView
  const words = text.split(' ')
  let open = false // *destaque* pode cobrir várias palavras
  return (
    <Tag ref={ref} className={`sh-words ${on ? 'is-in' : ''} ${className}`} aria-label={text.replace(/[*_]/g, '')}>
      {words.map((raw, i) => {
        if (raw.startsWith('*')) open = true
        const accent = open
        if (raw.endsWith('*')) open = false
        const mark = raw.includes('_')
        const word = raw.replace(/[*_]/g, '')
        const inner = <span className={accent || mark ? 'sh-accent' : undefined}>{word}</span>
        return (
          <Fragment key={i}>
            <span className="sh-word" aria-hidden style={{ '--i': i, '--d': `${delay}ms` } as CSSProperties}>
              {mark ? <Scribble on={on} delay={delay + words.length * 55 + 250}>{inner}</Scribble> : inner}
            </span>
            {i < words.length - 1 && ' '}
          </Fragment>
        )
      })}
    </Tag>
  )
}

/** Traço "feito à mão" desenhado sob a palavra. */
export function Scribble({ children, on, delay = 600 }: { children: ReactNode; on: boolean; delay?: number }) {
  return (
    <span className={`sh-scribble ${on ? 'is-in' : ''}`} style={{ '--d': `${delay}ms` } as CSSProperties}>
      {children}
      <svg viewBox="0 0 300 22" preserveAspectRatio="none" aria-hidden>
        <path d="M2 14 C 40 6, 90 4, 140 9 S 230 16, 298 6" pathLength={1} />
      </svg>
    </span>
  )
}

const easeOut = (x: number) => 1 - Math.pow(1 - x, 3)

/** Conta de 0 até `value` quando aparece (ou na montagem, com `immediate`). */
export function Counter({ value, decimals = 0, prefix = '', suffix = '', duration = 1400, immediate = false }: { value: number; decimals?: number; prefix?: string; suffix?: string; duration?: number; immediate?: boolean }) {
  const [ref, inView] = useInView<HTMLSpanElement>()
  const [n, setN] = useState(0)
  const on = immediate || inView
  useEffect(() => {
    if (!on) return
    if (reducedMotion()) { setN(value); return }
    let raf = 0
    const t0 = performance.now()
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration)
      setN(value * easeOut(p))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [on, value, duration])
  const txt = n.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  return <span ref={ref} className="sh-num">{prefix}{txt}{suffix}</span>
}

/** Brilho que segue o mouse dentro do cartão (só desktop; CSS lê --mx/--my). */
export function trackGlow(e: React.PointerEvent<HTMLElement>) {
  const r = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`)
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`)
}
